import { useTranslation } from "react-i18next";
import { Skeleton, Box, Typography } from "@mui/material";

import { ListItemIcon } from "../../assets/icons/go-hire-page-icons";

type SubmissionInfoItem = {
  title: string;
  list?: (string | number)[];
  description?: string | null;
};

const SubmissionInfo = ({
  idealCandidateInfoList,
  whoCanApplyList,
  isSubmissionInfoLoading,
}: {
  idealCandidateInfoList: SubmissionInfoItem[];
  whoCanApplyList: SubmissionInfoItem[];
  isSubmissionInfoLoading: boolean;
}) => {
  const { t } = useTranslation("goHire", { keyPrefix: "components.submissionInfo" });

  if (isSubmissionInfoLoading) {
    return (
      <Box component="section" sx={styles.container}>
        {[1, 2].map((group) => (
          <Box key={group} sx={styles.listContainer}>
            <Skeleton variant="text" sx={{ width: "150px", height: "24px", mb: 1 }} />
            {[1, 2, 3].map((item) => (
              <Box key={item} sx={styles.listItemsContainer}>
                <Skeleton variant="circular" sx={{ width: "20px", height: "20px" }} />
                <Skeleton variant="text" sx={{ width: "200px" }} />
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  }

  const renderList = (list: SubmissionInfoItem[]) =>
    list.map((info, index) => (
      <Box key={index} sx={styles.listItemWrapper}>
        <Box sx={styles.listItemsContainer}>
          <ListItemIcon />
          <Typography sx={styles.listText}>{info.title}</Typography>
        </Box>
        {info.description && <Typography sx={styles.listDescription}>{info.description}</Typography>}
        {info.list && info.list.length > 0 && (
          <Box sx={styles.subListContainer}>
            {info.list.map((item, i) => (
              <Typography key={i} sx={styles.subListText}>
                {item}
                {i < info.list!.length - 1 ? ", " : ""}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    ));

  return (
    <Box component="section" sx={styles.container}>
      {idealCandidateInfoList.length > 0 && (
        <Box component="ul" sx={styles.listContainer}>
          <Typography sx={styles.listTitle}>{t("requiredSubmissionInfo")}</Typography>
          {renderList(idealCandidateInfoList)}
        </Box>
      )}
      {whoCanApplyList.length > 0 && (
        <Box component="ul" sx={styles.listContainer}>
          <Typography sx={styles.listTitle}>{t("whoCanApply")}</Typography>
          {renderList(whoCanApplyList)}
        </Box>
      )}
    </Box>
  );
};

export default SubmissionInfo;

const styles = {
  container: {
    width: "100%",
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    gap: "24px",
  },
  listTitle: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  listContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  listItemWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  listItemsContainer: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  listText: {
    color: "var(--Dark-Mode---100, #1E2021)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  listDescription: {
    color: "var(--Dark-Mode---100, #1E2021)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
    paddingLeft: "32px",
  },
  subListContainer: {
    display: "flex",
    flexWrap: "wrap",
    paddingLeft: "32px",
    gap: "2px",
  },
  subListText: {
    color: "var(--Dark-Mode---100, #1E2021)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
  },
};
